// ПРИМЕР ЦЕПОЧКИ В СЦЕНАХ — ПЯТЬ СЛУЧАЙНЫХ СООБЩЕНИЙ НАБОРА, ПОТОМ «КУПИТЬ» (QUICK-START §8.22, владелец 28.10.2026:
// «каждый раз, когда показываешь пример цепочки, показывай рандомно сообщений 5 из набора, а потом уже слово „Купить“»).
//
// Сцены с цепочкой — g2 («Продажи») и s5 («Цепочка рассылок»). Сценарий несёт набор (`series.pool`: заготовки серии
// «Быстрого запуска» QS_TYPES, отрендеренные `renderQuickText`) и отрезок шагов серии (`from`…`to`: от первого «дня» до
// последнего, не включая его): при каждом показе на устройстве (без сети) отрезок заменяется пятью случайными
// сообщениями набора без повторов, по порядку групп набора; последний «день», сообщение продажи с «Купить» и итог
// сцены остаются её собственными. Старая сборка поле `series` не читает и играет шаги как есть.
//
// INVARIANT(scriptera): выборка — ОДНА функция на сайт (`expandSeries`) и её зеркало в Swift (`SceneScript.pickingSeries`):
// тот же отрезок, то же правило порядка, тот же вид вставки (день → сообщение → кнопка типа; §10.3 — кнопка у каждого). Причина:
// сцена одна на тур, мастер и «Быструю настройку», веб и iOS — «пиксель в пиксель». Канон: lib/scene-contract.ts.
import { QS_GROUPS, QS_TYPES, type QsLocale } from '@/lib/quick-start/catalog';
import { renderQuickText, type QsRenderCtx } from '@/lib/quick-start/render';
import type { SceneDef, SceneStep } from '@/lib/scene-script';

/** Сколько сообщений набора показывает сцена за один показ (§8.22). */
export const SCENE_SERIES_PICK = 5;

/** Сообщение набора: номер группы (порядок `QS_GROUPS`), текст, подпись кнопки типа (нет — без кнопки). */
export interface SceneSeriesItem {
  g: number;
  text: string;
  chip?: string;
}

export interface SceneSeries {
  /** Отрезок шагов сценария, который заменяется выборкой: `from` — первый «день», `to` — последний «день» (не входит). */
  from: number;
  to: number;
  pick: number;
  /** Подпись «дня» перед каждым сообщением выборки — первый «день» отрезка. */
  day: string;
  pool: SceneSeriesItem[];
}

/** Сцены с примером цепочки. */
export const SERIES_SCENES = ['g2', 's5'] as const;

/** Группы, из которых берётся выборка: продажа — своя у сцены (последней, с «Купить»), возврат — после продажи. */
const POOL_GROUPS: ReadonlySet<string> = new Set(QS_GROUPS.filter((g) => g !== 'sale' && g !== 'return'));

const EMPTY_CTX: QsRenderCtx = {
  gift: '',
  product: '',
  productName: '',
  word: '',
  offer: '',
  result: '',
  how: '',
  term: '',
  evenIf: '',
  eventDate: '',
  eventTime: '',
  deadline: '',
};

/** Обязательные (вне `[[ ]]`) подстановки заготовки. */
function required(template: string): string[] {
  const outside = template.replace(/\[\[(?:(?!\]\]).)*\]\]/gs, '');
  return [...outside.matchAll(/\{(\w+)\}/g)].map((m) => m[1]!);
}

/**
 * Набор сцены: заготовки серии с подставленным оффером (`ctx`; нет — без оффера). В набор идут только сообщения, которые
 * складываются целиком: все обязательные подстановки заполнены и нет мест, которые клиент дописывает сам («…»).
 */
export function seriesPool(locale: QsLocale, ctx: Partial<QsRenderCtx> = {}): SceneSeriesItem[] {
  const full: QsRenderCtx = { ...EMPTY_CTX, ...ctx };
  const out: SceneSeriesItem[] = [];
  for (const t of QS_TYPES) {
    if (!POOL_GROUPS.has(t.group)) continue;
    // QUICK-START §10.9 (владелец 29.10: «CTA-кнопка одна на каждое сообщение»): кнопка есть у КАЖДОГО типа набора —
    // в наборе все складные типы (кроме продажи — она последней, и возврата — он после продажи), у каждого — его кнопка.
    const text = renderType(t.text[locale], full);
    if (!text) continue;
    out.push({ g: QS_GROUPS.indexOf(t.group), text, ...(t.button ? { chip: t.button.label[locale] } : {}) });
  }
  return out;
}

/** Заготовка целиком под оффер: все обязательные подстановки есть и нет мест «…», которые клиент дописывает сам. */
function renderType(tpl: string, full: QsRenderCtx): string {
  if (required(tpl).some((k) => !(full as unknown as Record<string, string>)[k]?.trim())) return '';
  const text = renderQuickText(tpl, full);
  return !text || text.includes('…') ? '' : text;
}

/** Подпись кнопки покупки набора (`QS_TYPES[].button`, тип «Цена и состав» — та же, что у всех сообщений «Купить»). */
function buyLabel(locale: QsLocale): string {
  return QS_TYPES.find((t) => t.id === 'price')?.button?.label[locale] ?? '';
}

/**
 * QUICK-START §10.3 п.3: последнее сообщение серии — сообщение группы «Продажа» с кнопкой «Купить» из набора: первое по
 * порядку набора, которое складывается под оффер целиком. Ни одно не складывается (оффера нет) — null: остаётся
 * собственное сообщение продажи сцены (`g2.m3` / `s5.m3`) с её кнопкой.
 */
export function seriesFinal(locale: QsLocale, ctx: Partial<QsRenderCtx> = {}): SceneSeriesItem | null {
  const full: QsRenderCtx = { ...EMPTY_CTX, ...ctx };
  const buy = buyLabel(locale);
  for (const t of QS_TYPES) {
    if (t.group !== 'sale' || !t.button || t.button.label[locale] !== buy) continue;
    const text = renderType(t.text[locale], full);
    if (text) return { g: QS_GROUPS.indexOf('sale'), text, chip: buy };
  }
  return null;
}

/** Сообщение продажи сцены после отрезка серии (бот → кнопка → ответ кнопкой) заменяется сообщением набора. */
function withFinal(steps: readonly SceneStep[], to: number, fin: SceneSeriesItem): SceneStep[] {
  const out = [...steps];
  const bot = out.findIndex((st, i) => i >= to && st.k === 'bot');
  if (bot < 0) return out;
  const chip = out.findIndex((st, i) => i > bot && st.k === 'chip');
  out[bot] = { k: 'bot', text: fin.text };
  if (chip >= 0 && fin.chip) {
    out[chip] = { k: 'chip', text: fin.chip };
    if (out[chip + 1]?.k === 'echo') out[chip + 1] = { k: 'echo', text: fin.chip };
  }
  return out;
}

/** Отрезок серии в шагах сцены: от первого «дня» до последнего; «дней» меньше двух — отрезка нет. */
function seriesRange(steps: readonly SceneStep[]): { from: number; to: number } | null {
  const days = steps.flatMap((st, i) => (st.k === 'day' ? [i] : []));
  if (days.length < 2) return null;
  return { from: days[0]!, to: days[days.length - 1]! };
}

/** Сцена с набором серии (g2, s5); прочие — как есть. */
export function withSeries(def: SceneDef, locale: QsLocale, ctx: Partial<QsRenderCtx> = {}): SceneDef {
  if (!(SERIES_SCENES as readonly string[]).includes(def.k)) return def;
  const range = seriesRange(def.steps);
  if (!range) return def;
  const fin = seriesFinal(locale, ctx);
  const steps = fin ? withFinal(def.steps, range.to, fin) : def.steps;
  const pool = seriesPool(locale, ctx);
  if (pool.length === 0) return fin ? { ...def, steps } : def;
  const first = def.steps[range.from];
  const day = first && first.k === 'day' ? first.text : '';
  return { ...def, steps, series: { ...range, pick: SCENE_SERIES_PICK, day, pool } };
}

/** У бота свои сообщения цепочки (слоты `msg1`–`msg4` фраз бота) — сцена показывает их, выборки нет. */
export function hasOwnLetters(texts: Partial<Record<string, string>> | null | undefined): boolean {
  return !!texts && ['msg1', 'msg2', 'msg3', 'msg4'].some((k) => !!texts[k]?.trim());
}

/** Сцена без выборки (свои сообщения бота, статья справки): шаги — как есть. */
export function withoutSeries(def: SceneDef): SceneDef {
  if (!def.series) return def;
  const { series: _drop, ...rest } = def;
  return rest;
}

/**
 * Показ: отрезок серии → `pick` случайных сообщений набора без повторов, по порядку групп (внутри группы — порядок
 * набора), каждое — «день», сообщение, кнопка типа (если есть). `rnd` — источник случая (тест подставляет свой).
 */
export function expandSeries(def: SceneDef, rnd: () => number = Math.random): SceneDef {
  const s = def.series;
  if (!s || s.pool.length === 0 || s.from < 0 || s.to > def.steps.length || s.from >= s.to) return def;
  const idx = s.pool.map((_, i) => i);
  for (let i = idx.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [idx[i], idx[j]] = [idx[j]!, idx[i]!];
  }
  const picked = idx
    .slice(0, Math.min(s.pick, idx.length))
    .sort((a, b) => s.pool[a]!.g - s.pool[b]!.g || a - b)
    .map((i) => s.pool[i]!);
  const mid: SceneStep[] = picked.flatMap((it) => [
    { k: 'day' as const, text: s.day },
    { k: 'bot' as const, text: it.text },
    // §11.20 п.2: кнопки сообщений серии сцена не ждёт — ждёт только «Купить» в конце (шаг после отрезка).
    ...(it.chip ? [{ k: 'chip' as const, text: it.chip, auto: true as const }] : []),
  ]);
  const { series: _done, ...rest } = def;
  return { ...rest, steps: [...def.steps.slice(0, s.from), ...mid, ...def.steps.slice(s.to)] };
}
